
import { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface MoodTagsProps {
  selected: string[];
  onSelect: (selected: string[]) => void;
  moodRating: string;
  disabled?: boolean;
}

export function MoodTags({ 
  selected, 
  onSelect,
  moodRating,
  disabled = false
}: MoodTagsProps) {
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  
  // Update available tags based on mood rating
  useEffect(() => {
    switch(moodRating) {
      case "1": // Struggling
        setAvailableTags([
          "Anxious", "Stressed", "Sad", "Lost", "Overwhelmed", 
          "Depressed", "Fearful", "Hurt", "Lonely", "Doubting"
        ]);
        break;
      case "2": // Down
        setAvailableTags([
          "Tired", "Uncertain", "Confused", "Frustrated", 
          "Disappointed", "Worried", "Unmotivated", "Discontent"
        ]);
        break;
      case "3": // Neutral
        setAvailableTags([
          "Calm", "Reflective", "Waiting", "Hopeful", 
          "Patient", "Quiet", "Searching", "Curious"
        ]);
        break;
      case "4": // Good
        setAvailableTags([
          "Content", "Peaceful", "Optimistic", "Motivated", 
          "Confident", "Thankful", "Satisfied", "Encouraged"
        ]);
        break;
      case "5": // Excellent
        setAvailableTags([
          "Joyful", "Blessed", "Inspired", "Excited", "Fulfilled", 
          "Grateful", "Faithful", "Loving", "Purposeful"
        ]);
        break;
      default:
        setAvailableTags([]);
    }
    
    // Filter out any selected tags that are no longer available
    onSelect(selected.filter(tag => 
      availableTags.includes(tag)
    ));
  }, [moodRating]);

  const toggleTag = (tag: string) => {
    if (disabled) return;
    
    if (selected.includes(tag)) {
      onSelect(selected.filter(t => t !== tag));
    } else {
      onSelect([...selected, tag]);
    }
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium mb-1">
        How would you describe your feelings? (Select up to 3)
      </label>
      <div className="flex flex-wrap gap-2">
        {availableTags.map(tag => (
          <Badge
            key={tag}
            variant="outline"
            className={cn(
              "cursor-pointer py-1 px-3",
              selected.includes(tag) 
                ? "bg-blue-900/40 border-blue-700 text-blue-100" 
                : "bg-slate-900/30 border-slate-800 text-slate-300 hover:bg-slate-800/40",
              disabled && "opacity-50 cursor-not-allowed"
            )}
            onClick={() => toggleTag(tag)}
          >
            {tag}
          </Badge>
        ))}
      </div>
    </div>
  );
}
